// @givmo/app-charity — Tier-2 verification document upload (CPA-WEB2, plan A-1 Tier 2).
//
// The three-step signed-upload flow for a PENDING_REVIEW claim, mirroring the admin documents page's
// signed-URL pattern (there for DOWNLOAD; here for UPLOAD):
//   1. REQUEST → `charityAdminRequestVerificationUploadUrl`
//                (POST /charity-admin/claims/{id}/documents/upload-url) → `{ upload_url, object_ref,
//                method, content_type }`. A short-lived signed PUT into the RESTRICTED bucket.
//   2. PUT     → upload the file bytes DIRECTLY to `upload_url` with a bare `fetch` (NOT the
//                api-client — the bytes go straight to GCS, cross-origin, and must NOT carry the
//                Givmo Bearer / App Check headers or hit the same-origin guard). Method + Content-Type
//                come from the grant.
//   3. CONFIRM → `charityAdminConfirmVerificationUpload` (POST /charity-admin/claims/{id}/documents)
//                with `{ document_type, object_ref }` → records the `charity_verification_document`
//                row. The backend verifies the object exists before recording (no dangling ref).
//
// The doc bytes never pass through the backend; this component never renders a read URL (the sensitive
// bytes are super-admin-review-only). We show the REQUIRED type (the IRS determination letter)
// prominently and the optional types below, each a self-contained upload slot reflecting the
// already-uploaded docs from the claim's document list.

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { charityAdminListMyVerificationDocumentsOptions } from '@givmo/api-client/query';
import {
  sdk,
  isGivmoError,
  type CharityVerificationDocumentType,
  type VerificationDocumentOut,
  type VerificationDocumentUploadUrlOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { CheckCircle2, FileText, UploadCloud } from 'lucide-react';
import { extractDocuments } from './claim-decode';
import {
  CLAIM_REVIEW_SLA_COPY,
  DOC_TYPES,
  docStatusLabel,
  docStatusTone,
  docTypeLabel,
  type DocTypeMeta,
} from './claim-vocab';

const PREFIX = 'claim-docs';

/** `accept` for the file picker — PDF or an image (the backend allow-lists these MIME types). */
const ACCEPT = 'application/pdf,image/png,image/jpeg';
const ACCEPTED_MIME_TYPES = new Set(ACCEPT.split(','));
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB client-side guard (backend is authoritative).

export interface DocumentUploadProps {
  claimRequestId: string;
}

export function DocumentUpload({ claimRequestId }: DocumentUploadProps) {
  const queryClient = useQueryClient();

  const listOptions = charityAdminListMyVerificationDocumentsOptions({
    path: { claim_request_id: claimRequestId },
  });
  const docsQuery = useQuery({
    ...listOptions,
    select: (data: unknown) => extractDocuments(data),
  });
  const docs: VerificationDocumentOut[] = docsQuery.data ?? [];

  // Map already-uploaded docs by type so a slot can show its status.
  const byType = useMemo(() => {
    const map = new Map<string, VerificationDocumentOut>();
    for (const doc of docs) {
      // Keep the newest per type (list is newest-first by convention; first wins).
      if (!map.has(doc.document_type)) map.set(doc.document_type, doc);
    }
    return map;
  }, [docs]);

  const required = DOC_TYPES.filter((d) => d.required);
  const optional = DOC_TYPES.filter((d) => !d.required);

  // Documents whose type NO offered slot covers. The slots are driven by DOC_TYPES, so the moment a
  // type is retired from that list (OFFICER_AUTHORIZATION, 2026-08-29) any document already uploaded
  // under it stops being rendered by the loops below — it silently vanishes from the claimant's view
  // while still counting toward `docs.length`, which also suppresses the "No documents uploaded yet."
  // line. Listing them read-only keeps the panel's promise honest: it shows the state of YOUR
  // documents, not the state of the slots we currently offer.
  const offeredTypes = new Set<string>(DOC_TYPES.map((d) => d.value));
  // ONE row per retired type, newest first — the same rule `byType` applies to the offered slots.
  // There is NO unique constraint on (claim_request_id, document_type): confirming an upload INSERTs
  // a row, and Replace is the ordinary producer of a second one. Mapping `docs` directly would render
  // a duplicate row per replacement, each carrying an identical testid.
  const unmatched = useMemo(() => {
    const seen = new Set<string>();
    return docs.filter((doc) => {
      if (offeredTypes.has(doc.document_type) || seen.has(doc.document_type)) return false;
      seen.add(doc.document_type);
      return true;
    });
    // `offeredTypes` is derived from a module constant, so it is stable across renders.
  }, [docs]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: listOptions.queryKey });

  return (
    <Card testId={`${PREFIX}-panel`}>
      <CardHeader title="Verification documents" />
      <CardBody className="flex flex-col gap-16">
        <p className="text-s13 text-text-secondary">
          Upload your IRS determination letter (PDF or image). A reviewer will confirm your claim,
          usually within {CLAIM_REVIEW_SLA_COPY}.
        </p>
        {docsQuery.isPending ? (
          <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-loading`)}>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : docsQuery.isError ? (
          <div
            className="flex items-center justify-between gap-12 rounded-lg border border-danger-100 bg-danger-50 p-12"
            {...testIdProp(`${PREFIX}-error`)}
          >
            <ErrorMessage>
              {isGivmoError(docsQuery.error)
                ? docsQuery.error.message
                : 'We couldn’t load your uploaded documents.'}
            </ErrorMessage>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void docsQuery.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          </div>
        ) : (
          <>
            {docs.length === 0 ? (
              <p className="text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}-empty`)}>
                No documents uploaded yet.
              </p>
            ) : null}
            <div className="flex flex-col gap-10">
              {required.map((meta) => (
                <DocSlot
                  key={meta.value}
                  meta={meta}
                  existing={byType.get(meta.value) ?? null}
                  claimRequestId={claimRequestId}
                  onUploaded={invalidate}
                />
              ))}
            </div>

            <details
              className="rounded-lg border border-border-subtle"
              {...testIdProp(`${PREFIX}-optional`)}
            >
              <summary className="cursor-pointer select-none px-14 py-12 font-sans text-s13 font-w600 text-text-secondary">
                Additional documents (optional)
              </summary>
              <div className="flex flex-col gap-10 px-14 pb-14">
                {optional.map((meta) => (
                  <DocSlot
                    key={meta.value}
                    meta={meta}
                    existing={byType.get(meta.value) ?? null}
                    claimRequestId={claimRequestId}
                    onUploaded={invalidate}
                  />
                ))}
              </div>
            </details>

            {unmatched.length > 0 ? (
              <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-retired`)}>
                {unmatched.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-start gap-12 rounded-lg border border-border-subtle p-14"
                    {...testIdProp(`${PREFIX}-retired-${doc.document_type}`)}
                  >
                    {/* Mirror the slot's own rule: the success check belongs to an UPLOADED
                        document only. A PENDING_UPLOAD row wearing a green check beside "Not
                        uploaded yet" is unreachable today (confirm-upload writes UPLOADED) but the
                        icon must not be the thing asserting it. */}
                    <Icon
                      icon={doc.status === 'UPLOADED' ? CheckCircle2 : FileText}
                      size={20}
                      className={
                        doc.status === 'UPLOADED'
                          ? 'mt-1 shrink-0 text-success-600'
                          : 'mt-1 shrink-0 text-icon'
                      }
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-8">
                        <span className="font-w600 text-s14 text-text-strong">
                          {docTypeLabel(doc.document_type)}
                        </span>
                        <Badge tone={docStatusTone(doc.status)} size="sm" dot>
                          {docStatusLabel(doc.status)}
                        </Badge>
                      </div>
                      {/* No Replace control: we no longer ask for this document, so re-supplying it
                          is not a thing to offer. It stays visible because the charity sent it. */}
                      <p className="mt-2 text-s13 text-text-tertiary">
                        We no longer ask for this document. It stays on your claim for the reviewer.
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

interface DocSlotProps {
  meta: DocTypeMeta;
  existing: VerificationDocumentOut | null;
  claimRequestId: string;
  onUploaded: () => void | Promise<unknown>;
}

/** One upload slot for a single document type: pick a file → request URL → PUT → confirm. */
function DocSlot({ meta, existing, claimRequestId, onUploaded }: DocSlotProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);

  const upload = useMutation({
    mutationFn: async (file: File): Promise<VerificationDocumentOut> => {
      const contentType = file.type || 'application/octet-stream';

      // 1. Signed upload URL for this doc type.
      const { data: grant } = await sdk.charityAdminRequestVerificationUploadUrl({
        path: { claim_request_id: claimRequestId },
        body: {
          document_type: meta.value as CharityVerificationDocumentType,
          content_type: contentType,
        },
        throwOnError: true,
      });
      const upl: VerificationDocumentUploadUrlOut = grant;

      // 2. PUT the bytes straight to the restricted bucket (bare fetch — NOT the api-client; no
      //    Givmo auth/App Check headers, cross-origin to GCS).
      // ⚠ BOTH headers are part of the V4 signature. The backend signer bakes
      //    `Content-Disposition: attachment` in (identity.generate_verification_doc_upload_url)
      //    so the object can never be served inline, and every signed header lands in the
      //    canonical request — omitting one is a 403 SignatureDoesNotMatch that reads like a
      //    permissions problem. The board and compliance uploaders both send it; this one did
      //    not, so the upload could never have succeeded even once CSP + bucket CORS allowed
      //    the request out.
      const putResponse = await fetch(upl.upload_url, {
        method: upl.method ?? 'PUT',
        headers: {
          'Content-Type': upl.content_type ?? contentType,
          'Content-Disposition': 'attachment',
        },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload failed (${putResponse.status}). Please try again.`);
      }

      // 3. Confirm → record the row (backend verifies the object exists first).
      const { data: confirmed } = await sdk.charityAdminConfirmVerificationUpload({
        path: { claim_request_id: claimRequestId },
        body: {
          document_type: meta.value as CharityVerificationDocumentType,
          object_ref: upl.object_ref,
        },
        throwOnError: true,
      });
      return confirmed;
    },
    onSuccess: async () => {
      setSelectedFile(null);
      setReplaceOpen(false);
      setFileError(null);
      toast.success(`${meta.label} uploaded`);
      await onUploaded();
    },
    onError: (error: unknown) => {
      // CLEAR the pick. Leaving it behind renders the filename under the slot, which reads
      // as "uploaded" — a failed upload then looks exactly like a successful one, and the
      // only signal to the contrary is a toast that auto-dismisses at the bottom of a tall
      // page. Surface the reason IN the slot instead, beside the control that failed.
      setSelectedFile(null);
      setReplaceOpen(false);
      const message = isGivmoError(error)
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : 'Could not upload the document. Please try again.';
      setFileError(message);
      toast.error(message);
    },
  });

  const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!ACCEPTED_MIME_TYPES.has(file.type)) {
      setFileError('Please choose a PDF, PNG, or JPEG file.');
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError('That file is over 15 MB. Please upload a smaller file.');
      setSelectedFile(null);
      return;
    }
    setFileError(null);
    setSelectedFile(file);
    if (existing) setReplaceOpen(true);
    else upload.mutate(file);
  };

  const uploaded = existing != null;

  return (
    <>
      <div
        className="flex items-start gap-12 rounded-lg border border-border-subtle p-14"
        {...testIdProp(`${PREFIX}-slot-${meta.value}`)}
      >
        <Icon
          icon={uploaded ? CheckCircle2 : FileText}
          size={20}
          className={uploaded ? 'mt-1 shrink-0 text-success-600' : 'mt-1 shrink-0 text-icon'}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-8">
            <span className="font-w600 text-s14 text-text-strong">{meta.label}</span>
            {meta.required ? (
              <Badge tone="neutral" size="sm">
                Required
              </Badge>
            ) : null}
            {existing ? (
              <Badge tone={docStatusTone(existing.status)} size="sm" dot>
                {docStatusLabel(existing.status)}
              </Badge>
            ) : null}
          </div>
          <p className="mt-2 text-s13 text-text-tertiary">{meta.description}</p>
          {selectedFile ? (
            <p
              className="mt-4 truncate text-s13 text-text-secondary"
              {...testIdProp(`${PREFIX}-selected-${meta.value}`)}
            >
              {upload.isPending
                ? `Uploading ${selectedFile.name}…`
                : `Ready to upload: ${selectedFile.name}`}
            </p>
          ) : null}
          {fileError ? <p className="mt-4 text-s13 text-danger-600">{fileError}</p> : null}
        </div>
        <div className="shrink-0">
          <Button
            appearance="saas"
            tone={uploaded ? 'secondary' : 'primary'}
            size="sm"
            loading={upload.isPending}
            leadingIcon={<Icon icon={UploadCloud} size={14} aria-hidden />}
            onClick={() => inputRef.current?.click()}
            testId={`${PREFIX}-upload-${meta.value}`}
          >
            {uploaded ? 'Replace' : 'Upload'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={onPick}
            {...testIdProp(`${PREFIX}-input-${meta.value}`)}
          />
        </div>
      </div>
      <Dialog.Root
        open={replaceOpen}
        onOpenChange={(open) => {
          if (upload.isPending) return;
          setReplaceOpen(open);
          if (!open) setSelectedFile(null);
        }}
      >
        <Dialog.Content aria-label={`Replace ${meta.label}`} testId={`${PREFIX}-replace-dialog`}>
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-4">
              <Dialog.Title className="font-w700 text-h6 text-text-strong">
                Replace {meta.label}?
              </Dialog.Title>
              <Dialog.Description className="text-s14 text-text-secondary">
                This will replace the document currently available for review.
              </Dialog.Description>
            </div>
            <div className="flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={upload.isPending}
                onClick={() => {
                  setReplaceOpen(false);
                  setSelectedFile(null);
                }}
                testId={`${PREFIX}-replace-cancel`}
              >
                Keep current document
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                loading={upload.isPending}
                onClick={() => selectedFile && upload.mutate(selectedFile)}
                testId={`${PREFIX}-replace-confirm`}
              >
                Replace document
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
